// @flow

import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from '@riseart/common';
import { ArtDetails, ArtDetailsItem } from '@riseart/art';
import { FilterService } from '@riseart/filter-service';
import { product as ENUM_PRODUCT, filter as FILTER_ENUM } from 'Enum';
import { getFilterDomainConfig } from 'shared_models/configs/filters/domains';
import { validatedUrlParams } from 'shared_services/riseart/utils/FilterRouteUtils';

const { TYPE_ORIGINAL } = ENUM_PRODUCT.type;

/**
 * handlerFilterValues
 * @param {Array<Object>} value
 * @param {Function} formatMessage
 * @param {Object} filterService
 */
function handlerFilterValues(value: Array<Object>, formatMessage: Function, filterService: Object) {
  let accumulatedFilterPathValues = {};

  return value
    .filter((i) => !!i.value)
    .map(({ key, value }, idx) => {
      const translationTitle = formatMessage({ id: `filters.art.${key}.${value}` });
      accumulatedFilterPathValues = { ...accumulatedFilterPathValues, [key]: [value] };
      const validatedFilters = validatedUrlParams(
        accumulatedFilterPathValues,
        filterService.getConfig().dimensions,
      );

      return (
        <React.Fragment key={key}>
          {idx ? ' > ' : null}
          {Object.keys(validatedFilters).length ? (
            <Link to={filterService.assemble(validatedFilters, {})} title={translationTitle}>
              {translationTitle}
            </Link>
          ) : (
            translationTitle
          )}
        </React.Fragment>
      );
    });
}

/**
 * ART_DETAIL_KEYS
 * translation keys and handlers to combine data for each spec items
 */
const ART_DETAIL_KEYS = [
  {
    translationKey: 'type',
    handler(value, formatMessage) {
      return formatMessage({
        id: `components.art.art_types.${
          value === TYPE_ORIGINAL ? `original_art` : `limited_edition`
        }`,
      });
    },
  },
  { translationKey: 'medium', dataKey: 'mediumWithSubMedium', handler: handlerFilterValues },
  { translationKey: 'style', dataKey: 'styleStructured', handler: handlerFilterValues },
  { translationKey: 'subject', dataKey: 'subjectWithSubSubject', handler: handlerFilterValues },
  { translationKey: 'year' },
  {
    translationKey: 'editionSize',
    handler(value, formatMessage) {
      return formatMessage({ id: 'components.art.edition_of_short' }, { editionSize: value });
    },
  },
  { translationKey: 'size', dataKey: 'artworkDimensionsLabel' },
  {
    alwaysShow: true,
    translationKey: 'readyToHang',
    handler(value, formatMessage) {
      return formatMessage({ id: `common.${value ? 'yes' : 'no'}` });
    },
  },
  {
    alwaysShow: true,
    translationKey: 'framed',
    handler(value, formatMessage) {
      return value
        ? `${formatMessage({ id: 'common.yes' })}${
            value.description ? `, ${value.description}` : ``
          }`
        : `${formatMessage({ id: 'common.no' })}`;
    },
  },
  {
    alwaysShow: true,
    translationKey: 'signed',
    handler(value, formatMessage) {
      return value
        ? `${formatMessage({ id: 'common.yes' })}${
            value.description ? `, ${value.description}` : ``
          }`
        : `${formatMessage({ id: 'common.no' })}`;
    },
  },
  { translationKey: 'materials' },
  {
    translationKey: 'shipping',
    handler(value) {
      return value;
    },
  },
];

type Props = { data: Object };

/**
 * ArtSpecs
 *
 * @param {Props} props
 */
export const ArtSpecs = ({ data = {} }: Props) => {
  const { formatMessage } = useIntl();
  const filterService = new FilterService(
    getFilterDomainConfig(FILTER_ENUM.domain.ART),
    formatMessage,
  );

  return (
    <ArtDetails nodeType="ul">
      {ART_DETAIL_KEYS.map(({ translationKey, dataKey, handler, alwaysShow }: Object) => {
        const value = data[dataKey || translationKey];

        if (!value && !alwaysShow) {
          return null;
        }

        return (
          <ArtDetailsItem
            key={translationKey}
            separator=":&nbsp;&nbsp;"
            name={formatMessage({ id: `components.art.details.${translationKey}` })}
          >
            {handler ? handler(value, formatMessage, filterService) : value}
          </ArtDetailsItem>
        );
      })}
    </ArtDetails>
  );
};
